import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Phone, MapPin } from "lucide-react";

const LAST_UPDATED = "24 October 2026";

export default function PrivacyPolicy() {
  useEffect(() => { document.title = "Privacy Policy — ZeroLabs Digitals"; }, []);

  return (
    <div data-testid="page-privacy" className="pt-24 pb-24">
      <section className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/10 text-[#3B82F6] flex items-center justify-center">
            <ShieldCheck size={22}/>
          </div>
          <div>
            <div className="eyebrow">Legal</div>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-1">Privacy Policy</h1>
          </div>
        </div>
        <p className="mt-3 text-sm text-[#8C859E] font-mono">Last updated: {LAST_UPDATED}</p>

        <div className="mt-10 space-y-8 text-[#D6D2E1] leading-relaxed">
          <Section title="1. Introduction">
            <p>
              This Privacy Policy explains how <strong className="text-white">ZeroLabs Digitals</strong> ("we", "us", "our")
              collects, uses, shares and protects information about you when you visit
              <span className="text-[#22D3EE]"> zerolabsdigi.in</span>, use our products such as
              School Management System and MediQ, or interact with our AI assistant "Zero" via chat or voice call.
            </p>
            <p className="mt-3">
              We comply with the <strong className="text-white">Digital Personal Data Protection Act, 2023</strong> (DPDP Act) of India
              and follow generally accepted global standards for data protection.
            </p>
          </Section>

          <Section title="2. Information we collect">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-white">Contact form submissions</strong> — name, email, phone number, company, service interest and message.</li>
              <li><strong className="text-white">Consultation bookings</strong> — name, email, phone, company, chosen service, preferred date and time, and any notes you share.</li>
              <li><strong className="text-white">AI chat conversations</strong> — text you send to Zero and the responses generated for you. Stored to improve our service and provide continuity if you return.</li>
              <li><strong className="text-white">AI voice-call data</strong> — when our AI voice agent calls you, we may record the call, generate a transcript, and store a short summary. You will always be informed at the start of the call.</li>
              <li><strong className="text-white">Product usage</strong> — if you sign in to a ZeroLabs product (e.g., School Management System, MediQ), we store the account information you provide and metadata like login times.</li>
              <li><strong className="text-white">Technical data</strong> — IP address, browser type, device, referral URL, and pages you visit. Used for security, analytics and troubleshooting.</li>
              <li><strong className="text-white">Cookies</strong> — small files that keep you signed in, remember preferences and help us measure site traffic. See Section 8.</li>
            </ul>
          </Section>

          <Section title="3. How we use your information">
            <ul className="list-disc pl-5 space-y-2">
              <li>To respond to your enquiries and provide the services you request.</li>
              <li>To confirm and schedule your consultation (including sending calendar invites).</li>
              <li>To have our AI voice agent, Zero, call you shortly after you submit the contact form — so we can understand your needs faster.</li>
              <li>To operate, secure, monitor and improve our website and products.</li>
              <li>To send transactional emails (booking confirmations, password resets, account notices). We do not send marketing emails without your consent.</li>
              <li>To comply with applicable laws and to enforce our Terms of Service.</li>
            </ul>
          </Section>

          <Section title="4. Legal basis for processing">
            <p>We process your personal data on one or more of the following bases:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong className="text-white">Consent</strong> — you voluntarily submit information (e.g., contact form, booking form) knowing it will be used to contact you.</li>
              <li><strong className="text-white">Legitimate business interest</strong> — running and improving our services, preventing fraud and ensuring security.</li>
              <li><strong className="text-white">Legal obligation</strong> — where retention is required by Indian tax, corporate or telecom regulations.</li>
            </ul>
          </Section>

          <Section title="5. Third-party sub-processors">
            <p>We use trusted third parties to operate our services. Each is contractually bound to protect your data.</p>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
                <thead className="bg-white/5 text-[#22D3EE] text-left">
                  <tr>
                    <th className="p-3 font-semibold">Sub-processor</th>
                    <th className="p-3 font-semibold">Purpose</th>
                    <th className="p-3 font-semibold">Region</th>
                  </tr>
                </thead>
                <tbody className="[&>tr]:border-t [&>tr]:border-white/10">
                  <tr><td className="p-3">OpenAI / Anthropic</td><td className="p-3">AI chat & voice responses</td><td className="p-3">USA</td></tr>
                  <tr><td className="p-3">MongoDB Atlas</td><td className="p-3">Database hosting</td><td className="p-3">Mumbai / Global</td></tr>
                  <tr><td className="p-3">Resend</td><td className="p-3">Transactional email</td><td className="p-3">USA / EU</td></tr>
                  <tr><td className="p-3">Emergent Cloud</td><td className="p-3">Application hosting</td><td className="p-3">Global</td></tr>
                  <tr><td className="p-3">Vobiz / Sarvam AI (planned)</td><td className="p-3">AI voice calling & TTS</td><td className="p-3">India</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-[#8C859E]">We do not sell your personal data to any third party.</p>
          </Section>

          <Section title="6. Data retention">
            <p>We keep your data only as long as needed for the purposes described above, or as required by law:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Contact and booking records — up to <strong className="text-white">3 years</strong>, then anonymised.</li>
              <li>AI chat & voice transcripts — up to <strong className="text-white">12 months</strong> unless linked to an active engagement.</li>
              <li>Product account data — until you delete your account or 3 years of inactivity.</li>
              <li>Financial and tax records — as required by Indian law (typically 8 years).</li>
            </ul>
          </Section>

          <Section title="7. Your rights">
            <p>Under the DPDP Act you have the right to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong className="text-white">Access</strong> the personal data we hold about you.</li>
              <li><strong className="text-white">Correct</strong> inaccurate or incomplete data.</li>
              <li><strong className="text-white">Erase</strong> your data, subject to legal exceptions.</li>
              <li><strong className="text-white">Withdraw consent</strong> at any time — future processing based on that consent will stop.</li>
              <li><strong className="text-white">Nominate</strong> another person to exercise your rights in the event of death or incapacity.</li>
              <li><strong className="text-white">Grievance redressal</strong> — write to our Grievance Officer (Section 11).</li>
            </ul>
            <p className="mt-3">To exercise any right, email <a className="text-[#22D3EE] hover:underline" href="mailto:info@zerolabsdigi.in">info@zerolabsdigi.in</a>. We respond within 15 working days.</p>
          </Section>

          <Section title="8. Cookies & tracking">
            <p>
              We use essential cookies to keep you logged in and remember your preferences. We may also use privacy-friendly
              analytics to understand which pages help visitors most. We do <strong className="text-white">not</strong>
              use third-party advertising cookies or cross-site trackers. You can clear cookies at any time from your browser settings.
            </p>
          </Section>

          <Section title="9. Security">
            <p>
              All data is transmitted over HTTPS. Passwords are salted and hashed with bcrypt. Access to production systems is
              restricted to authorised personnel using multi-factor authentication. Backups are encrypted at rest.
              While no system is 100% secure, we follow industry-standard controls to protect your information.
            </p>
          </Section>

          <Section title="10. Children">
            <p>
              Our services are not directed to individuals under 18. If you believe a child has provided us with personal data
              without parental consent, please contact us and we will delete the information promptly.
            </p>
          </Section>

          <Section title="11. Grievance Officer">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 mt-2 space-y-2">
              <p className="text-white font-semibold">ZeroLabs Digitals — Grievance Officer</p>
              <p className="text-sm flex items-center gap-2"><Mail size={14} className="text-[#22D3EE]"/> <a className="text-[#22D3EE] hover:underline" href="mailto:info@zerolabsdigi.in">info@zerolabsdigi.in</a></p>
              <p className="text-sm flex items-center gap-2"><Phone size={14} className="text-[#22D3EE]"/> <a className="text-[#22D3EE] hover:underline" href="tel:+918971452224">+91 8971452224</a></p>
              <p className="text-sm flex items-start gap-2"><MapPin size={14} className="mt-1 text-[#22D3EE] shrink-0"/><span>4172, 3rd Floor, 17th Main, 5th Cross, MCC B Block, Davangere — 577004, Karnataka, India.</span></p>
            </div>
          </Section>

          <Section title="12. Changes to this Policy">
            <p>
              We may update this Privacy Policy from time to time. Material changes will be highlighted on this page and,
              where required, notified to you by email. The date at the top always reflects the current version.
            </p>
          </Section>
        </div>

        <div className="mt-14 text-sm text-[#8C859E]">
          See also our <Link to="/terms" className="text-[#22D3EE] hover:underline">Terms of Service</Link>.
        </div>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl md:text-2xl font-semibold text-white tracking-tight mb-3">{title}</h2>
      <div className="text-[#D6D2E1] text-[15px]">{children}</div>
    </section>
  );
}
